import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  Search,
  X,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Mic,
  Bot,
  User,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Play,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

// ── Helpers ──────────────────────────────────────────────────────────────────
const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
};

const STATUS_COLORS = {
  Initiated: 'bg-blue-100 text-blue-700',
  Answered: 'bg-green-100 text-green-700',
  'No Answer': 'bg-gray-100 text-gray-600',
  Busy: 'bg-yellow-100 text-yellow-700',
  Rejected: 'bg-orange-100 text-orange-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Failed: 'bg-red-100 text-red-700',
};

const OUTCOME_COLORS = {
  Interested: 'bg-green-100 text-green-700',
  'Not Interested': 'bg-red-100 text-red-700',
  'Callback Required': 'bg-yellow-100 text-yellow-700',
};

const fmtDuration = (secs) => {
  if (!secs && secs !== 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

// ── Sub-components ────────────────────────────────────────────────────────────
const Badge = ({ label, colorClass }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
    {label}
  </span>
);

const ContactSearchDropdown = ({ onSelect, placeholder = 'Search contacts…' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiFetch(`/api/crm/contacts/search?q=${encodeURIComponent(query)}`);
        setResults(data.contacts || data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const pick = (c) => {
    onSelect(c);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF]"
        />
        {loading && (
          <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.map((c) => (
            <button
              key={c.id || c._id}
              onClick={() => pick(c)}
              className="w-full text-left px-4 py-2.5 hover:bg-purple-50 flex flex-col border-b border-gray-50 last:border-0"
            >
              <span className="text-sm font-medium text-gray-800">{c.name || c.full_name}</span>
              <span className="text-xs text-gray-500">{c.organization || c.org} · {c.mobile || c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Tab 1: Initiate Call ──────────────────────────────────────────────────────
const InitiateCallTab = () => {
  const [contact, setContact] = useState(null);
  const [callMode, setCallMode] = useState('ai');
  const [calling, setCalling] = useState(false);
  const [activeCall, setActiveCall] = useState(null); // { call_sid, log_id, status, elapsed }
  const [elapsed, setElapsed] = useState(0);
  const [outcomeForm, setOutcomeForm] = useState(false);
  const [outcome, setOutcome] = useState({ status: '', outcome: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const startTimer = () => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };

  const stopAll = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
  }, []);

  useEffect(() => () => stopAll(), [stopAll]);

  const initiateCall = async () => {
    if (!contact) return;
    setCalling(true);
    setError('');
    try {
      const data = await apiFetch('/api/crm/voice/call', {
        method: 'POST',
        body: JSON.stringify({ contact_id: contact.id || contact._id, call_mode: callMode }),
      });
      setActiveCall({ call_sid: data.call_sid, log_id: data.log_id, status: 'Initiated' });
      startTimer();
      // Poll status every 5s
      pollRef.current = setInterval(async () => {
        try {
          const logs = await apiFetch(`/api/crm/voice/logs?contact_id=${contact.id || contact._id}&limit=1`);
          const latest = (logs.logs || logs)[0];
          if (latest) {
            setActiveCall((prev) => ({ ...prev, status: latest.status }));
            if (['Completed', 'Failed', 'No Answer', 'Rejected', 'Busy'].includes(latest.status)) {
              stopAll();
              setOutcomeForm(true);
            }
          }
        } catch { /* silent */ }
      }, 5000);
    } catch (e) {
      setError('Failed to initiate call. Please try again.');
    } finally {
      setCalling(false);
    }
  };

  const submitOutcome = async () => {
    if (!activeCall?.log_id) return;
    setSubmitting(true);
    setError('');
    try {
      await apiFetch(`/api/crm/voice/logs/${activeCall.log_id}/outcome`, {
        method: 'PUT',
        body: JSON.stringify(outcome),
      });
      setSuccess('Outcome saved successfully.');
      setActiveCall(null);
      setOutcomeForm(false);
      setOutcome({ status: '', outcome: '', notes: '' });
      setContact(null);
    } catch {
      setError('Failed to save outcome.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Contact Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Contact</h3>
        <ContactSearchDropdown onSelect={setContact} />
        {contact && (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-100 rounded-lg flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">{contact.name || contact.full_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{contact.organization || contact.org}</p>
              <p className="text-xs text-[#843FEF] mt-0.5 font-medium">{contact.mobile || contact.phone}</p>
            </div>
            <button onClick={() => setContact(null)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Call Mode */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Call Mode</h3>
        <div className="flex gap-4">
          {[
            { value: 'ai', label: 'AI Voice', icon: Bot },
            { value: 'human', label: 'Human', icon: User, soon: true },
          ].map(({ value, label, icon: Icon, soon }) => (
            <label
              key={value}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                callMode === value ? 'border-[#843FEF] bg-purple-50' : 'border-gray-200 hover:border-purple-200'
              }`}
            >
              <input
                type="radio"
                name="callMode"
                value={value}
                checked={callMode === value}
                onChange={() => setCallMode(value)}
                className="accent-[#843FEF]"
              />
              <Icon className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{label}</span>
              {soon && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                  Coming Soon
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Active Call Display */}
      {activeCall && (
        <div className="bg-white rounded-xl border border-[#843FEF]/30 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Call in Progress</p>
              <p className="text-xs text-gray-500">SID: {activeCall.call_sid}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-lg font-mono font-bold text-gray-800">{fmtDuration(elapsed)}</p>
              <Badge label={activeCall.status} colorClass={STATUS_COLORS[activeCall.status] || 'bg-gray-100 text-gray-600'} />
            </div>
          </div>
        </div>
      )}

      {/* Outcome Form */}
      {outcomeForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">Record Call Outcome</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Call Status</label>
            <select
              value={outcome.status}
              onChange={(e) => setOutcome((o) => ({ ...o, status: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
            >
              <option value="">Select status…</option>
              {Object.keys(STATUS_COLORS).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
            <select
              value={outcome.outcome}
              onChange={(e) => setOutcome((o) => ({ ...o, outcome: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
            >
              <option value="">Select outcome…</option>
              {Object.keys(OUTCOME_COLORS).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              rows={3}
              value={outcome.notes}
              onChange={(e) => setOutcome((o) => ({ ...o, notes: e.target.value }))}
              placeholder="Add call notes…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 resize-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setOutcomeForm(false); setActiveCall(null); }}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
            >
              Skip
            </button>
            <button
              onClick={submitOutcome}
              disabled={submitting}
              className="px-4 py-2 text-sm bg-[#843FEF] text-white rounded-lg hover:bg-[#7230d4] disabled:opacity-60 font-medium"
            >
              {submitting ? 'Saving…' : 'Save Outcome'}
            </button>
          </div>
        </div>
      )}

      {/* Initiate Button */}
      {!activeCall && !outcomeForm && (
        <button
          onClick={initiateCall}
          disabled={!contact || calling}
          className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-[#843FEF] text-white rounded-xl font-semibold hover:bg-[#7230d4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          <Phone className="w-5 h-5" />
          {calling ? 'Initiating Call…' : 'Initiate Call'}
        </button>
      )}
    </div>
  );
};

// ── Tab 2: Bulk Calls ─────────────────────────────────────────────────────────
const BulkCallsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const addContact = (c) => {
    if (contacts.find((x) => (x.id || x._id) === (c.id || c._id))) return;
    setContacts((prev) => [...prev, c]);
  };

  const removeContact = (id) => setContacts((prev) => prev.filter((c) => (c.id || c._id) !== id));

  const bulkCall = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/crm/voice/bulk-call', {
        method: 'POST',
        body: JSON.stringify({ contact_ids: contacts.map((c) => c.id || c._id) }),
      });
      setProgress(data);
      setContacts([]);
    } catch {
      setError('Bulk call failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {progress && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Bulk Call Progress</h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Total', value: progress.total, color: 'text-gray-800' },
              { label: 'Initiated', value: progress.initiated, color: 'text-green-600' },
              { label: 'Failed', value: progress.failed, color: 'text-red-500' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center p-3 bg-gray-50 rounded-lg">
                <p className={`text-2xl font-bold ${color}`}>{value ?? '—'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Add Contacts</h3>
        <ContactSearchDropdown onSelect={addContact} placeholder="Search and add contacts…" />
        {contacts.length > 100 && (
          <div className="mt-2 flex items-center gap-2 text-amber-700 text-xs">
            <AlertCircle className="w-3.5 h-3.5" /> Maximum 100 contacts allowed per bulk call.
          </div>
        )}
      </div>

      {contacts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Selected Contacts <span className="ml-1 text-[#843FEF]">({contacts.length})</span>
            </h3>
            <button onClick={() => setContacts([])} className="text-xs text-red-500 hover:text-red-700">
              Clear All
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {contacts.map((c) => (
              <div
                key={c.id || c._id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{c.name || c.full_name}</p>
                  <p className="text-xs text-gray-500">{c.mobile || c.phone}</p>
                </div>
                <button onClick={() => removeContact(c.id || c._id)} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={bulkCall}
        disabled={contacts.length === 0 || contacts.length > 100 || loading}
        className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-[#843FEF] text-white rounded-xl font-semibold hover:bg-[#7230d4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
      >
        <Mic className="w-5 h-5" />
        {loading ? 'Starting Bulk Call…' : `Start Bulk Call (${contacts.length})`}
      </button>
    </div>
  );
};

// ── Update Outcome Modal ──────────────────────────────────────────────────────
const OutcomeModal = ({ log, onClose, onSaved }) => {
  const [form, setForm] = useState({
    status: log.status || '',
    outcome: log.outcome || '',
    lead_score: log.lead_score || '',
    sentiment: log.sentiment || '',
    ai_summary: log.ai_summary || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/crm/voice/logs/${log.id || log._id}/outcome`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      onSaved();
    } catch {
      setError('Failed to update outcome.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-800">Update Call Outcome</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {error && (
          <div className="mb-4 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <div className="space-y-4">
          {[
            { label: 'Status', key: 'status', options: Object.keys(STATUS_COLORS) },
            { label: 'Outcome', key: 'outcome', options: Object.keys(OUTCOME_COLORS) },
            { label: 'Sentiment', key: 'sentiment', options: ['Positive', 'Neutral', 'Negative'] },
          ].map(({ label, key, options }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <select
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
              >
                <option value="">Select…</option>
                {options.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Lead Score (0-100)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.lead_score}
              onChange={(e) => setForm((f) => ({ ...f, lead_score: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">AI Summary</label>
            <textarea
              rows={3}
              value={form.ai_summary}
              onChange={(e) => setForm((f) => ({ ...f, ai_summary: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 resize-none"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#843FEF] text-white rounded-lg hover:bg-[#7230d4] disabled:opacity-60 font-medium"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tab 3: Call Logs ──────────────────────────────────────────────────────────
const CallLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', outcome: '', date_from: '', date_to: '' });
  const [selectedLog, setSelectedLog] = useState(null);
  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, ...filters });
      // Remove empty params
      [...params.entries()].forEach(([k, v]) => { if (!v) params.delete(k); });
      params.set('page', page);
      params.set('limit', limit);
      const data = await apiFetch(`/api/crm/voice/logs?${params.toString()}`);
      setLogs(data.logs || data || []);
      setTotal(data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      {selectedLog && (
        <OutcomeModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onSaved={() => { setSelectedLog(null); fetchLogs(); }}
        />
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" /> Filters:
          </div>
          <select
            value={filters.status}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          >
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            value={filters.outcome}
            onChange={(e) => { setFilters((f) => ({ ...f, outcome: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          >
            <option value="">All Outcomes</option>
            {Object.keys(OUTCOME_COLORS).map((o) => <option key={o}>{o}</option>)}
          </select>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          />
          <button
            onClick={() => { setFilters({ status: '', outcome: '', date_from: '', date_to: '' }); setPage(1); }}
            className="text-sm text-[#843FEF] hover:underline ml-auto"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-[#843FEF] animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Contact', 'Mobile', 'Status', 'Outcome', 'Duration', 'Initiated At', 'Recording'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">No call logs found.</td>
                  </tr>
                ) : logs.map((log) => (
                  <tr
                    key={log.id || log._id}
                    onClick={() => setSelectedLog(log)}
                    className="hover:bg-purple-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {log.contact_name || log.contact?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{log.mobile || log.contact?.mobile || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge label={log.status || '—'} colorClass={STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-600'} />
                    </td>
                    <td className="px-4 py-3">
                      {log.outcome ? (
                        <Badge label={log.outcome} colorClass={OUTCOME_COLORS[log.outcome] || 'bg-gray-100 text-gray-600'} />
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono">{fmtDuration(log.duration)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(log.initiated_at || log.created_at)}</td>
                    <td className="px-4 py-3">
                      {log.recording_url ? (
                        <a
                          href={log.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-[#843FEF] hover:underline text-xs"
                        >
                          <Play className="w-3 h-3" /> Play
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const TABS = [
  { id: 'initiate', label: 'Initiate Call', icon: Phone },
  { id: 'bulk', label: 'Bulk Calls', icon: Mic },
  { id: 'logs', label: 'Call Logs', icon: Clock },
];

const CRMVoiceCalling = () => {
  const [activeTab, setActiveTab] = useState('initiate');

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Phone className="w-6 h-6 text-[#843FEF]" />
          Voice Calling
        </h1>
        <p className="text-sm text-gray-500 mt-1">AI-powered voice calls and call log management</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm mb-6 w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-[#843FEF] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'initiate' && <InitiateCallTab />}
      {activeTab === 'bulk' && <BulkCallsTab />}
      {activeTab === 'logs' && <CallLogsTab />}
    </div>
  );
};

export default CRMVoiceCalling;
